"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Building2,
  Crown,
  Vote,
  Shield,
  Briefcase,
  Users,
  Globe2,
  ScrollText,
  Sparkles,
  ArrowRight,
  Target,
  Compass,
  Activity,
  Award,
  Crosshair,
  Mic,
  GraduationCap,
  Heart,
  Layers,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
};

const SECTORS = [
  {
    icon: Building2,
    title: "Governments",
    body: "Federal ministries, state departments, and sovereign institutions requiring unified command over perception, privacy, intelligence, and governance delivery. The full five-pillar architecture — Perception, Privacy, Politics, Policing, Policy — activates simultaneously.",
    href: "/sectors/governments/",
    accent: "red",
  },
  {
    icon: Shield,
    title: "Defense & Intelligence",
    body: "Military, paramilitary, law enforcement, and national security agencies operating where intelligence asymmetry determines national outcomes. Quantum-resistant encryption, real-time threat detection, multi-agency fusion, and cyber forensics at the speed of operations.",
    href: "/sectors/defense-intelligence/",
    accent: "emerald",
  },
  {
    icon: Crown,
    title: "Royal Families & Dynasties",
    body: "Reigning monarchies, royal houses, and sovereign households where reputation is generational and discretion is non-negotiable. National security level confidentiality, zero security incidents across fifteen-plus years, and absolute architectural commitment to privacy.",
    href: "/sectors/kingdoms-royal-families/",
    accent: "purple",
  },
  {
    icon: Vote,
    title: "Political Organizations",
    body: "National and international parties, candidates, and coalitions engineering electoral outcomes. The crucible in which the operational architecture was forged — fifteen victories in eighteen engagements, nine hundred million voters reached, zero security breaches.",
    href: "/sectors/political-organizations/",
    accent: "cyan",
  },
  {
    icon: ScrollText,
    title: "Presidential & PM Offices",
    body: "Executive offices at the apex of sovereign power — where every decision radiates outward, shaping economies and defining national narratives. Sovereign-grade encryption, real-time threat intelligence, national perception management, and inter-agency coordination at the speed of decisions.",
    href: "/sectors/presidential-pm-offices/",
    accent: "amber-gold",
  },
  {
    icon: Briefcase,
    title: "Corporate & Enterprise",
    body: "Fortune 100 enterprises and multinational corporations operating across jurisdictions. Brand protection at continental scale, crisis response measured in hours, competitive intelligence that conventional firms cannot provide, and digital transformation without the consulting trap.",
    href: "/sectors/corporate-enterprise/",
    accent: "denim",
  },
  {
    icon: Users,
    title: "Public Figures & HNWIs",
    body: "Celebrities, business leaders, public intellectuals, and ultra-wealthy private clients for whom exposure is a catastrophe and discretion is survival. Reputation architecture, sovereign privacy systems, encrypted communications, and strategic advisory as a unified operational framework.",
    href: "/sectors/public-figures-hnwis/",
    accent: "coral",
  },
  {
    icon: Globe2,
    title: "International Organizations",
    body: "Multilateral bodies and global institutions coordinating governance across sovereign boundaries. Administrative modernization across jurisdictions, multi-jurisdictional policy coordination, institutional perception management, and sovereign data protection at population scale.",
    href: "/sectors/international-organizations/",
    accent: "lime",
  },
];

const HEADLINE_STATS = [
  { metric: "15", suffix: "/18", label: "Campaign Victories", sub: "Across sovereign engagements", accent: "red" },
  { metric: "300", suffix: "+", label: "Elite Clients", sub: "Eight distinct sectors", accent: "cyan" },
  { metric: "18", suffix: "", label: "Countries", sub: "Three continents", accent: "amber-gold" },
  { metric: "0", suffix: "", label: "Security Incidents", sub: "Fifteen-plus years", accent: "emerald" },
  { metric: "900", suffix: "M+", label: "Voters Reached", sub: "Twenty-two languages", accent: "purple" },
  { metric: "99.9999", suffix: "%", label: "Uptime", sub: "31.5 sec max/year", accent: "denim" },
];

const WHY_THIS_MATTERS = [
  {
    icon: Crosshair,
    title: "Sovereign-Grade Discipline",
    body: "Every sector engagement operates at the same standard: the discipline that protects national security communications, that has produced verified electoral outcomes, and that has maintained zero security breaches across fifteen-plus years. The standard does not adjust based on client profile.",
    accent: "red",
  },
  {
    icon: Compass,
    title: "Integrated Architecture, Not Point Solutions",
    body: "The organizations that engage this work cannot afford fragmented tools. They require an integrated architecture where intelligence, perception, security, and operations function as one closed-loop system. The integration is the moat. The orchestration is the advantage.",
    accent: "cyan",
  },
  {
    icon: Activity,
    title: "Outcomes, Not Deliverables",
    body: "The distinction that defines this work: outcomes are measured by what changed in the world, not what was produced in the engagement. Fifteen electoral victories. Zero security incidents. Ninety-nine point nine nine nine nine percent uptime. Policy implementation rates rising from thirty-four to ninety-four percent. These are verified outcomes.",
    accent: "emerald",
  },
  {
    icon: Award,
    title: "National Security Level Confidentiality",
    body: "The discretion that defines this work means clients are described by category, not by name. From first contact through engagement completion, the operational discipline maintains absolute compartmentalization. The standard is not enterprise confidentiality. It is sovereign confidentiality.",
    accent: "purple",
  },
];

const SECTOR_ACTIVATION = [
  { sector: "Governments", pillars: ["Perception", "Privacy", "Politics", "Policing", "Policy"], note: "All five pillars", accent: "red" },
  { sector: "Defense & Intelligence", pillars: ["Policing", "Privacy"], note: "Two foundational pillars", accent: "emerald" },
  { sector: "Royal Families", pillars: ["Perception", "Privacy"], note: "Two pillars, absolute discretion", accent: "purple" },
  { sector: "Political Organizations", pillars: ["Perception", "Privacy", "Politics", "Policing", "Policy"], note: "All five pillars", accent: "cyan" },
  { sector: "Presidential & PM Offices", pillars: ["Perception", "Privacy", "Policing", "Policy"], note: "Four-pillar activation", accent: "amber-gold" },
  { sector: "Corporate & Enterprise", pillars: ["Perception", "Privacy", "Policy"], note: "Three-pillar configuration", accent: "denim" },
  { sector: "Public Figures & HNWIs", pillars: ["Perception", "Privacy"], note: "Two pillars, personal sovereignty", accent: "coral" },
  { sector: "International Organizations", pillars: ["Policy", "Perception"], note: "Two pillars, multilateral reach", accent: "lime" },
];

const ENGAGEMENT_MODEL = [
  {
    num: "01",
    title: "Intelligence",
    body: "Every engagement begins with intelligence, not assumptions. The ground truth is discovered through multi-source monitoring, field validation, and stakeholder mapping before any strategy is formed.",
    accent: "red",
  },
  {
    num: "02",
    title: "Analysis",
    body: "Intelligence without analysis is noise. Pattern identification, anomaly detection, and causal analysis separate the signal from the noise. The symptom is never the disease.",
    accent: "purple",
  },
  {
    num: "03",
    title: "Strategy",
    body: "The Penta P framework is deployed. Each pillar receives its specific role. Each platform receives its activation sequence. Strategy is not a document — it is an operational plan driving coordinated action.",
    accent: "cyan",
  },
  {
    num: "04",
    title: "Orchestration",
    body: "The neural command interface coordinates every platform simultaneously, reducing decision time from days to under one hour. Multi-disciplinary teams operate in unison through compartmentalized workflows.",
    accent: "emerald",
  },
  {
    num: "05",
    title: "Amplification",
    body: "Content deploys at scale through platform-optimized channels with organic authenticity. The signal becomes louder than the noise — reaching audiences most likely to be influenced, through channels most likely to resonate.",
    accent: "amber-gold",
  },
  {
    num: "06",
    title: "Feedback",
    body: "Every output is monitored, measured, and fed back into the intelligence stage. The system that enters the next engagement is smarter than the one that entered the last. The loop never breaks.",
    accent: "denim",
  },
];

const DIFFERENTIATORS = [
  {
    icon: Target,
    title: "Clinical Operating System",
    body: "Six clinical principles applied across every sector: diagnose before treating, triage under pressure, protocol with judgment, own the outcome, composure as infrastructure, do no harm. The medical method is the actual operating system — not a metaphor applied to business.",
    accent: "red",
  },
  {
    icon: Globe2,
    title: "Eighteen Countries, Three Continents",
    body: "The infrastructure that took a decade to build operates across the world's most demanding environments — nations facing active conflict, governments managing political transitions, defense agencies confronting asymmetric threats with limited resources. The capability that survives these environments serves any sector.",
    accent: "cyan",
  },
  {
    icon: Shield,
    title: "Zero-Dependency Architecture",
    body: "Every platform owned. Every line of code controlled. Every capability proprietary. No external platform, no off-the-shelf solution, no licensed technology contributes to the core capabilities. Integration is the moat. Sovereignty is the standard.",
    accent: "emerald",
  },
  {
    icon: Users,
    title: "The 70% Operating Model",
    body: "Teams built across all sectors are, in most offices, over seventy percent women. This is not a diversity initiative. It is an operating decision with a decade of evidence — the talent the world overlooks is the talent that outperforms. The argument is from operations, not morality.",
    accent: "coral",
  },
];

export default function SectorsHubPage() {
  return (
    <>
      <PageHero
        icon={Building2}
        overline="Sectors Hub"
        overlineClassName="text-red"
        title={
          <>
            Eight Sectors. <span className="text-gradient-premium">One Operating System.</span>
          </>
        }
        subtitle="The comprehensive map of who Dr. Jyoti Kush serves as Chief Operating Officer — governments, royal families, political leaders, defense agencies, executive offices, Fortune-scale corporations, public figures, and international organizations. Each sector draws from a tailored configuration of the five-pillar framework. The discipline and the standard do not change."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Sectors" }]}
        badge="National security level confidentiality"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
              <Sparkles className="size-3.5 text-purple" />
              <span className="text-overline text-purple">Executive Summary</span>
            </div>
            <h2 className="text-display-md mb-5">
              Eight sectors served with sovereign-grade discipline.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Dr. Jyoti Kush operates as Chief Operating Officer at the intersection of strategy and execution — translating technological capability into client outcomes across eight distinct sectors. Each sector is defined by its operational requirements, its stakeholder complexity, and the configuration of capabilities required to serve it. The medical method — diagnose before treating, triage under pressure, follow protocol while preserving judgment, own the outcome, do no harm — is the operating system that governs every engagement regardless of sector.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The organizations that engage this work do not receive advice. They receive verified outcomes delivered through integrated platform architecture built over fifteen-plus years. The infrastructure was constructed for the most demanding environments on earth — nations facing active conflict, governments managing political transitions, defense agencies confronting asymmetric threats. The capability that survives these environments is the capability that serves any sector.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The discretion that defines this work means clients are described by category, not by name. National security level confidentiality is non-negotiable. From first contact through engagement completion, the operational discipline maintains absolute compartmentalization.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {HEADLINE_STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${ACCENT[s.accent].bg} to-transparent border ${ACCENT[s.accent].border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-sm font-bold ${ACCENT[s.accent].text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Building2 className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Eight Sectors</span>
          </div>
          <h2 className="text-display-md mb-4">The complete map of engagements.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each sector draws from a tailored configuration of the five-pillar framework. The integration is the moat. The orchestration is the advantage. No single-domain specialist occupies this space.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SECTORS.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link href={s.href} className="block h-full group">
                  <div className={`relative h-full glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}>
                    <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                    <div className="relative">
                      <div className="size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all">
                        <s.icon className="size-5 text-white" />
                      </div>
                      <h3 className={`text-heading-md mb-2 ${a.text}`}>{s.title}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed mb-3">{s.body}</p>
                      <div className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                        Explore <ArrowRight className="size-3" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Crosshair className="size-3.5 text-red" />
            <span className="text-overline text-red">Why This Matters</span>
          </div>
          <h2 className="text-display-md mb-4">The principles that govern every engagement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four structural commitments that define the work — applied across all eight sectors without exception. The standard does not adjust based on client profile, engagement size, or geographic location.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {WHY_THIS_MATTERS.map((w, i) => {
            const a = ACCENT[w.accent];
            return (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative flex items-start gap-4">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg`}>
                    <w.icon className="size-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{w.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{w.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Layers className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Pillar Activation by Sector</span>
          </div>
          <h2 className="text-display-md mb-4">How the five pillars configure per sector.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The five-pillar framework — Perception, Privacy, Politics, Policing, Policy — is not activated uniformly. Each sector draws from a tailored configuration calibrated to the operational requirements and stakeholder complexity of that domain.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {SECTOR_ACTIVATION.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.sector}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <h3 className={`text-heading-md ${a.text}`}>{s.sector}</h3>
                  <span className={`text-overline ${a.text} shrink-0`}>{s.note}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {s.pillars.map((p) => (
                    <span key={p} className={`inline-flex items-center px-2 py-0.5 rounded-full text-caption font-semibold ${a.text} border ${a.border} bg-background/30`}>
                      {p}
                    </span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Activity className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">The Six-Stage Methodology</span>
            </div>
            <h2 className="text-display-md mb-5">The battle-tested operating system.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Every engagement — regardless of sector, jurisdiction, or stakes — follows the same six-stage operating system refined over hundreds of sovereign-scale deployments. The stages are sequential in logic but iterative in practice. Intelligence flows continuously through all stages. Feedback from later stages refines earlier ones.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The methodology closes a loop that never breaks. The system that enters the next engagement is smarter than the one that entered the last. Every outcome — win or loss — adds to the training corpus. Every cycle produces more precise intelligence than the one before.
            </p>
          </div>
          <div className="space-y-3">
            {ENGAGEMENT_MODEL.map((s, i) => {
              const a = ACCENT[s.accent];
              return (
                <motion.div
                  key={s.num}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg ring-2 ${a.ring}`}>
                      <span className="text-body-md font-bold text-white">{s.num}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{s.title}</h3>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Award className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Core Differentiators</span>
          </div>
          <h2 className="text-display-md mb-4">What distinguishes this work.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Four structural advantages — verified through fifteen-plus years of sovereign-scale operations, fifteen campaign victories, and zero security incidents — that separate this operational architecture from conventional consulting, technology vendors, and advisory practices.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {DIFFERENTIATORS.map((d, i) => {
            const a = ACCENT[d.accent];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3.5">
                  <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <d.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{d.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{d.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Sparkles className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Cross-Sector Convergence</span>
          </div>
          <h2 className="text-display-md mb-4">The compound value of integration.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The eight sectors are not isolated practices. The intelligence architecture that serves defense agencies also serves Fortune 500 corporations. The perception management that protects royal families also protects public figures. The convergence creates compound value that no point solution can deliver.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { metric: "8", suffix: "", label: "Sectors", sub: "One framework", accent: "red" },
            { metric: "60", suffix: "+", label: "Integrated Services", sub: "Five pillars", accent: "purple" },
            { metric: "100", suffix: "%", label: "Proprietary Stack", sub: "Zero dependencies", accent: "cyan" },
            { metric: "15", suffix: "+", label: "Years Operational", sub: "Verified record", accent: "emerald" },
            { metric: "5", suffix: "", label: "Strategic Pillars", sub: "Closed-loop system", accent: "amber-gold" },
            { metric: "70", suffix: "%+", label: "Women in Teams", sub: "By selection, by results", accent: "coral" },
            { metric: "1", suffix: "", label: "Operating Method", sub: "Clinical discipline", accent: "denim" },
            { metric: "0", suffix: "", label: "Security Incidents", sub: "Across all sectors", accent: "lime" },
          ].map((e, i) => {
            const a = ACCENT[e.accent];
            return (
              <motion.div
                key={e.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} text-center`}
              >
                <div className={`text-display-md font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                <div className="text-caption text-muted-foreground">{e.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Heart className="size-3.5 text-lime" />
            <span className="text-overline text-lime">The 70% Operating Model</span>
          </div>
          <h2 className="text-display-md mb-4">People as the foundation of every system.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The teams built across all eight sectors are, in most offices, over seventy percent women. This is not a diversity initiative. It is an operating finding — a decade of evidence that the talent the world overlooks is the talent that outperforms.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { num: "01", title: "Communication", body: "Problems surface earlier because the team is willing to say the uncomfortable thing before it becomes a catastrophe." },
            { num: "02", title: "Risk Discipline", body: "Protocol adherence without sacrificing judgment. The 99.9999% uptime standard is the product of this discipline." },
            { num: "03", title: "Collaboration", body: "Internal energy goes outward, toward the mission, instead of inward, toward each other. Coordination efficiency rises." },
            { num: "04", title: "Retention", body: "Institutional knowledge preserved because the environment is one where people can stay and thrive." },
            { num: "05", title: "Ego Management", body: "Ego interferes less with execution. The systems function as designed, without distortions." },
          ].map((t, i) => (
            <motion.div
              key={t.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="relative glass-card rounded-2xl p-5 bg-gradient-to-br from-lime/10 to-transparent border border-lime/20"
            >
              <div className="text-display-sm font-bold text-lime/30 leading-none mb-3">{t.num}</div>
              <h3 className="text-heading-md mb-2 text-lime">{t.title}</h3>
              <p className="text-caption text-muted-foreground leading-relaxed">{t.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage Dr. Jyoti Kush.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Every sector engagement begins with the same discipline: national security level confidentiality from first contact, six-stage methodology applied without modification, and outcomes verified through operational data. The standard is sovereign. The approach is the same.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/about/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <GraduationCap className="size-4" />
                About Dr. Jyoti Kush
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "Fifteen domains of expertise. One integrated operating system.", href: "/expertise/", icon: Compass, accent: "red" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Sparkles, accent: "purple" },
          { title: "Services", description: "Speaking, advisory, board service, operational consulting.", href: "/services/", icon: Briefcase, accent: "cyan" },
          { title: "The 70% Project", description: "The operating case for the talent the world overlooks.", href: "/the-70-percent-project/", icon: Users, accent: "coral" },
        ]}
      />
    </>
  );
}
